import React from 'react';
import { motion } from 'framer-motion';
import { Languages, Heart, Trophy } from 'lucide-react';
import type { PassportSkillGroup } from '../../api/passport';
import PassportAiNote from './PassportAiNote';

interface SkillsViewProps {
  skills: PassportSkillGroup[];
  languages: string[];
  interests: string[];
  highlights: string[];
  className?: string;
}

// Group accent colors (cycled).
const GROUP_CHIP = [
  'bg-pink-50 text-pink-600 border-pink-100',
  'bg-rose-50 text-rose-600 border-rose-100',
  'bg-amber-50 text-amber-600 border-amber-100',
  'bg-indigo-50 text-indigo-600 border-indigo-100',
  'bg-emerald-50 text-emerald-600 border-emerald-100',
];

const Chip: React.FC<{ children: React.ReactNode; cls: string }> = ({ children, cls }) => (
  <span className={`text-[11px] sm:text-xs font-medium px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full border ${cls}`}>{children}</span>
);

const SkillsView: React.FC<SkillsViewProps> = ({
  skills,
  languages,
  interests,
  highlights,
  className = '',
}) => {
  const hasSkills = skills.some((g) => g.items.length > 0);
  const isEmpty = !hasSkills && languages.length === 0 && interests.length === 0 && highlights.length === 0;

  if (isEmpty) {
    return (
      <div className={`flex items-center justify-center py-16 text-slate-400 ${className}`}>
        <div className="text-center">
          <div className="text-2xl mb-1.5">🧩</div>
          <p className="text-xs">No skills extracted yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative max-w-2xl mx-auto px-4 py-5 sm:py-6 space-y-5 sm:space-y-7 ${className}`}>
      <PassportAiNote />
      {hasSkills && (
        <div className="space-y-3.5 sm:space-y-4">
          {skills.map((group, gi) => {
            if (group.items.length === 0) return null;
            const chipCls = GROUP_CHIP[gi % GROUP_CHIP.length];
            return (
              <motion.div
                key={group.group}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: gi * 0.05, duration: 0.25 }}
              >
                <h3 className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                  {group.group}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {group.items.map((s) => (
                    <Chip key={s} cls={chipCls}>{s}</Chip>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {languages.length > 0 && (
        <section>
          <h3 className="flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
            <Languages className="w-3.5 h-3.5" /> Languages
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {languages.map((l) => (
              <Chip key={l} cls="bg-slate-50 text-slate-600 border-slate-200">{l}</Chip>
            ))}
          </div>
        </section>
      )}

      {interests.length > 0 && (
        <section>
          <h3 className="flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
            <Heart className="w-3.5 h-3.5" /> Interests
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {interests.map((i) => (
              <Chip key={i} cls="bg-slate-50 text-slate-600 border-slate-200">{i}</Chip>
            ))}
          </div>
        </section>
      )}

      {highlights.length > 0 && (
        <section>
          <h3 className="flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
            <Trophy className="w-3.5 h-3.5" /> Highlights
          </h3>
          <ul className="space-y-2">
            {highlights.map((h, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.04, duration: 0.25 }}
                className="flex items-start gap-2 text-[13px] sm:text-sm text-slate-700"
              >
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-amber-300 to-yellow-500 flex-shrink-0" />
                <span>{h}</span>
              </motion.li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default SkillsView;
